<template>
  <nav
    v-if="links.length"
    ref="navEl"
    class="relative hidden border-t border-slate-200/80 lg:block"
    aria-label="Meniu"
  >
    <div class="flex items-center gap-1 py-2">
      <NuxtLink
        v-for="link in arranged.visible"
        :key="`${link.to}-${link.label}`"
        :to="shopPath(link.to)"
        class="shrink-0 rounded-full px-3 py-1.5 text-sm font-medium transition-colors"
        :class="isNavActive(link.to)
          ? 'bg-brand-50 text-brand-800'
          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'"
        @click="moreOpen = false"
      >
        {{ link.label }}
      </NuxtLink>

      <div v-if="arranged.overflow.length" ref="moreEl" class="relative shrink-0">
        <button
          type="button"
          class="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
          :aria-expanded="moreOpen"
          @click="moreOpen = !moreOpen"
        >
          Mai multe
          <UIcon name="i-heroicons-chevron-down" class="size-3.5" :class="moreOpen ? 'rotate-180' : ''" />
        </button>
        <div
          v-if="moreOpen"
          class="absolute right-0 top-full z-30 mt-2 min-w-52 rounded-2xl border border-slate-200 bg-white p-2 shadow-lg"
        >
          <NuxtLink
            v-for="link in arranged.overflow"
            :key="`more-${link.to}-${link.label}`"
            :to="shopPath(link.to)"
            class="block rounded-xl px-3 py-2 text-sm font-medium"
            :class="isNavActive(link.to)
              ? 'bg-brand-50 text-brand-800'
              : 'text-slate-700 hover:bg-slate-50'"
            @click="moreOpen = false"
          >
            {{ link.label }}
          </NuxtLink>
        </div>
      </div>
    </div>

    <div
      ref="measureEl"
      class="pointer-events-none invisible absolute left-0 top-0 flex w-max items-center gap-1"
      aria-hidden="true"
    >
      <span
        v-for="link in links"
        :key="`measure-${link.to}-${link.label}`"
        data-nav-item
        class="shrink-0 rounded-full px-3 py-1.5 text-sm font-medium"
      >{{ link.label }}</span>
      <span data-nav-more class="shrink-0 rounded-full px-3 py-1.5 text-sm font-medium">Mai multe</span>
    </div>
  </nav>
</template>

<script setup lang="ts">
const props = defineProps<{
  links: Array<{ label: string, to: string }>
}>()

const route = useRoute()
const shopPath = useShopPath()
const navEl = ref<HTMLElement | null>(null)
const measureEl = ref<HTMLElement | null>(null)
const moreEl = ref<HTMLElement | null>(null)
const moreOpen = ref(false)
const visibleCount = ref(props.links.length)

const arranged = computed(() => {
  const links = props.links
  const count = Math.max(0, Math.min(visibleCount.value, links.length))
  if (count >= links.length) return { visible: links, overflow: [] as typeof links }
  const visible = links.slice(0, count)
  const overflow = links.slice(count)
  const activeIndex = links.findIndex((link) => isNavActive(link.to))
  if (activeIndex >= count && visible.length) {
    const [current] = overflow.splice(activeIndex - count, 1)
    overflow.unshift(visible[visible.length - 1])
    visible[visible.length - 1] = current
  }
  return { visible, overflow }
})

function navPath(to: string) {
  return shopPath(to).split('?')[0].replace(/\/+$/, '') || '/'
}

function isNavActive(to: string) {
  const target = navPath(to)
  const current = route.path.replace(/\/+$/, '') || '/'
  const home = navPath('/')
  if (target === home) return current === home
  return current === target || current.startsWith(`${target}/`)
}

function measure() {
  const nav = navEl.value
  const measure = measureEl.value
  if (!nav || !measure || nav.clientWidth === 0) return
  const items = [...measure.querySelectorAll<HTMLElement>('[data-nav-item]')]
  const more = measure.querySelector<HTMLElement>('[data-nav-more]')
  const styles = getComputedStyle(measure)
  const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0
  const moreWidth = more?.offsetWidth ?? 0
  const available = nav.clientWidth
  let used = 0
  let count = 0
  for (let index = 0; index < items.length; index += 1) {
    const width = items[index].offsetWidth + (count > 0 ? gap : 0)
    const reserve = index < items.length - 1 ? moreWidth + gap : 0
    if (used + width + reserve > available) break
    used += width
    count += 1
  }
  visibleCount.value = count
  if (count >= props.links.length) moreOpen.value = false
}

let observer: ResizeObserver | null = null

function onPointerDown(event: PointerEvent) {
  if (!moreOpen.value) return
  const target = event.target
  if (target instanceof Node && moreEl.value?.contains(target)) return
  moreOpen.value = false
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') moreOpen.value = false
}

onMounted(() => {
  measure()
  requestAnimationFrame(measure)
  document.fonts?.ready.then(() => measure()).catch(() => undefined)
  if (navEl.value && typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(() => measure())
    observer.observe(navEl.value)
  }
  document.addEventListener('pointerdown', onPointerDown)
  window.addEventListener('keydown', onKeydown)
})

watch(() => props.links, () => {
  visibleCount.value = props.links.length
  nextTick(measure)
}, { deep: true })

watch(() => route.fullPath, () => {
  moreOpen.value = false
  nextTick(measure)
})

onBeforeUnmount(() => {
  observer?.disconnect()
  document.removeEventListener('pointerdown', onPointerDown)
  window.removeEventListener('keydown', onKeydown)
})
</script>
